import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { FormikProps } from "formik/dist/types";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import KeywordSuggestionsDropdown from "src/components/common/KeywordSuggestionsDropdown";
import InsightsEditor from "src/components/dashboard/brief/rightSection/InsightsEditor";
import { getActivePlan } from "src/slices/auth/thunks";
import { languages, locations, usStates } from "src/slices/brief/helpers";
import { clearSuggestions } from "src/slices/keywordSuggestions";
import {
  selectKeywordSuggestions,
  selectKeywordSuggestionsError,
  selectKeywordSuggestionsLoading,
} from "src/slices/keywordSuggestions/selectors";
import { fetchKeywordSuggestions } from "src/slices/keywordSuggestions/thunks";
import { getProjects, projects } from "src/slices/projects";
import { selectOptimizeContentLoading } from "src/slices/suggestions/selectors";
import {
  generateAutomatedInstructionsForOptimize,
  scrapeArticleForOptimize,
} from "src/slices/suggestions/thunks";
import { snackAdded } from "src/slices/system";
import { useDispatch, useSelector } from "src/store";
type Props = {
  formik: FormikProps<{
    keyword: string;
    location: (typeof locations)[0];
    state: (typeof usStates)[0] | null;
    language: string;
    project: {
      projectName: string;
      projectId: string;
      projectDomain: string;
      isGSCIntegrated: boolean;
      companyDetails: any;
      GSCsiteUrl: string;
      excludedLinks: string[];
    };
    articleLink: string;
    stats: boolean;
    insights: string;
    interlinking: boolean;
    humanize: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
  }>;
};

const ChooseKeyword = ({ formik }: Props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const filter = createFilterOptions();
  const Project = formik.values.project;

  let Projects = useSelector(projects);
  const updatedProjects = [
    ...Projects,
    {
      projectId: "new",
      projectName: "Add new project",
      projectDomain: "",
      isGSCIntegrated: false,
      GSCsiteUrl: "",
      createdAt: "",
      updatedAt: "",
      briefCount: 0,
      briefs: [],
      AImodel: "claude",
      excludedLinks: [],
      brandVoice: "",
      companyDetails: {
        companyName: "",
        companyInformation: "",
        productInformation: "",
        targetAudience: "",
      },
      isSeoMissionCreated: false,
      competitors: [],
      optimizedArticleCount: 0,
    },
  ];
  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [isInterLinkingAdded, setIsInterLinkingAdded] = useState(false);
  const [isHumanizeAvailableAtPlan] = useState(true);
  const [humanizeToggletext] = useState(
    `Once your article is generated, our system proofreads, rewrites, and adjusts the text using AI to sound more natural and human-like, helping it avoid AI detection.
    <br /><br />
    Note: Headings and structure may change to improve readability. This is an automated process.`
  );
  // const currentPlan: any = useSelector(selectCurrentPlan);
  const getCurrentPlan = async () => {
    const currentPlan = (await dispatch(getActivePlan())) as any;
    setCurrentPlan(currentPlan?.payload?.plan);
  };
  useEffect(() => {
    getCurrentPlan();
  }, []);

  const [insights, setInsights] = useState(false);

  // Keyword suggestions state
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const keywordSuggestions = useSelector(selectKeywordSuggestions);
  const suggestionsLoading = useSelector(selectKeywordSuggestionsLoading);
  const suggestionsError = useSelector(selectKeywordSuggestionsError);
  const isOptimizeContentLoading = useSelector(selectOptimizeContentLoading);
  const [statsToggletext, setStatsToggletext] = useState(
    "We'll research and add relevant stats and quotes to your AI article if they match your outline."
  );
  const [interLinkingToggletext, setInterLinkingToggletext] = useState("");
  const [placeholderText, setPlaceholderText] = useState(
    "Add your insights here..."
  );
  const [aiResearchAgentToggletext, setAiResearchAgentToggletext] = useState(
    "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
  );
  const [aiEvalRefineAgentToggletext, setAiEvalRefineAgentToggletext] =
    useState(
      "Enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
    );
  const isStatsAvailableAtPlan =
    currentPlan?.stats === "true" || currentPlan?.stats === true;
  const isInterLinkingAvailableAtPlan =
    currentPlan?.interlinking === "true" || currentPlan?.interlinking === true;
  const isInsightsAvailableAtPlan =
    currentPlan?.insights === "true" || currentPlan?.insights === true;
  const isAiResearchAgentAvailableAtPlan =
    currentPlan?.aiResearchAgent === true ||
    currentPlan?.aiResearchAgent === "true";
  const handleInsightsAdded = (event: ChangeEvent<any>) => {
    setInsights(event.target.checked);
    formik.setFieldValue("brief.insights", "");
  };

  // Function to extract clean, structured outline from markdown content
  const extractOutlineFromMarkdown = (markdownText: string): string => {
    if (!markdownText) return "";

    const lines = markdownText.split("\n");
    const outlineLines: string[] = [];

    // Patterns to filter out promotional/implementation content
    const skipPatterns = [
      /subscribe|newsletter|inbox|email/i,
      /follow us|social media|linkedin|twitter/i,
      /download|ebook|whitepaper|resource/i,
      /contact|sales|demo|trial/i,
      /advertisement|sponsored|affiliate/i,
      /click here|learn more|read more/i,
      /\b(yarooms|specific product names)\b/i, // Filter out specific product mentions
      /^[-*+]\s*(provide|use|create|implement|add|install|arrange|train)\b/i, // Implementation details
      /^[-*+]\s*\w+\s*(rates?|percentages?|statistics?|metrics?|numbers?)/i, // Specific metrics
      /^[-*+]\s*(fitness|nutrition|sleep|apps?|platforms?|tools?)\b/i, // Tactical tools
    ];

    // Quality filters for content
    const isQualityContent = (line: string): boolean => {
      const trimmed = line.trim();

      // Skip empty lines
      if (!trimmed) return false;

      // Skip very short lines (likely incomplete)
      if (trimmed.length < 10) return false;

      // Skip lines that are just numbers or symbols
      if (/^[\d\s\-#*+.]+$/.test(trimmed)) return false;

      // Skip promotional content
      if (skipPatterns.some((pattern) => pattern.test(trimmed))) return false;

      return true;
    };

    // Extract headings with hierarchy preservation
    const extractHeadings = (line: string): boolean => {
      const trimmed = line.trim();
      const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);

      if (headingMatch) {
        const level = headingMatch[1].length;
        const title = headingMatch[2].trim();

        // Skip headings that are just promotional or meta content
        if (skipPatterns.some((pattern) => pattern.test(title))) return false;

        // Focus on strategic content (avoid overly specific implementation)
        if (level <= 3 && title.length > 5) {
          // Only include h1, h2, h3 with meaningful titles
          return true;
        }
      }
      return false;
    };

    // Extract strategic bullet points (avoid implementation details)
    const isStrategicBulletPoint = (line: string): boolean => {
      const trimmed = line.trim();
      const bulletMatch = trimmed.match(/^[-*+]\s+(.+)$/);

      if (bulletMatch) {
        const content = bulletMatch[1].trim();

        // Focus on high-level strategic points, not implementation details
        const strategicKeywords = [
          /\b(strategy|approach|framework|principle|benefit|advantage|outcome|result|impact|improvement)\b/i,
          /\b(increased?|improved?|enhanced?|reduced?|better|higher|lower|more effective)\b/i,
          /\b(organizational|company|business|workplace|employee|team|culture)\b/i,
        ];

        // Include if it contains strategic keywords and isn't too tactical
        return (
          strategicKeywords.some((pattern) => pattern.test(content)) &&
          !/^(provide|use|create|implement|add|install|arrange|train|set up)/i.test(
            content
          )
        );
      }
      return false;
    };

    let currentSection = "";

    for (const line of lines) {
      if (!isQualityContent(line)) continue;

      const trimmedLine = line.trim();

      // Extract headings
      if (extractHeadings(line)) {
        outlineLines.push(trimmedLine);
        currentSection = trimmedLine;
      }
      // Extract strategic bullet points only for relevant sections
      else if (isStrategicBulletPoint(line) && currentSection) {
        // Only include strategic bullets under major sections
        const headingLevel = currentSection.match(/^#+/)?.[0]?.length || 0;
        if (headingLevel <= 2) {
          // Only for h1 and h2 sections
          outlineLines.push(trimmedLine);
        }
      }
    }

    // Clean up and organize the outline
    const cleanOutline = outlineLines
      .filter((line, index) => {
        // Remove duplicate consecutive lines
        if (index > 0 && line === outlineLines[index - 1]) return false;
        return true;
      })
      .slice(0, 50); // Limit to prevent overly long outlines

    return cleanOutline.join("\n");
  };

  // Automated instructions generation handler
  const handleWriteInstructions = async () => {
    const articleLink = formik.values.articleLink;
    const projectId = formik.values.project?.projectId;

    if (!articleLink) {
      dispatch(
        snackAdded({
          message: "Please provide an article link to generate instructions",
          type: "error",
          duration: 3000,
        })
      );
      return;
    }

    if (!projectId) {
      dispatch(
        snackAdded({
          message: "Project information missing",
          type: "error",
          duration: 3000,
        })
      );
      return;
    }

    try {
      // First, scrape the article to get title, description, and content
      dispatch(
        snackAdded({
          message: "Scraping article content...",
          type: "info",
          duration: 2000,
        })
      );

      const scrapeResult = await dispatch(
        scrapeArticleForOptimize(articleLink)
      ).unwrap();

      if (!scrapeResult.success) {
        throw new Error(
          scrapeResult.error || "Failed to scrape article content"
        );
      }

      // Parse the response data - the scraper returns an array with [content, title, description]
      const articleContent = scrapeResult.data?.[0] || "";
      const articleTitle = scrapeResult.data?.[1] || "";
      const articleDescription = scrapeResult.data?.[2] || "";

      if (!articleContent) {
        throw new Error("No article content found");
      }

      // Notify user that scraping completed successfully
      dispatch(
        snackAdded({
          message: "Article scraped successfully. Generating instructions...",
          type: "success",
          duration: 2000,
        })
      );

      // Extract outline from the markdown content
      const outline = extractOutlineFromMarkdown(articleContent);

      // Generate instructions using the scraped data
      const instructions = await dispatch(
        generateAutomatedInstructionsForOptimize({
          projectId: projectId,
          title: articleTitle,
          description: articleDescription,
          outline: outline,
        })
      ).unwrap();

      // Update the insights field with generated instructions
      const currentInsights = formik.values.insights || "";
      let updatedInsights = "";

      if (currentInsights.trim()) {
        // If there are existing insights, add generated instructions below them
        updatedInsights = currentInsights + "\n\n---\n\n" + instructions;
      } else {
        // If no existing insights, add generated instructions at the top
        updatedInsights = instructions;
      }

      formik.setFieldValue("insights", updatedInsights);

      dispatch(
        snackAdded({
          message: "Instructions generated successfully",
          type: "success",
          duration: 3000,
        })
      );
    } catch (error) {
      console.error("Failed to generate instructions:", error);

      let errorMessage = "Failed to generate instructions. Please try again.";

      // Provide more specific error messages
      if (
        error.message?.includes("authenticate") ||
        error.message?.includes("403")
      ) {
        errorMessage =
          "Authentication failed. Please make sure you're logged in and try again.";
      } else if (error.message?.includes("scrape")) {
        errorMessage =
          "Failed to scrape article content. Please check the URL and try again.";
      } else if (
        error.message?.includes("network") ||
        error.message?.includes("timeout")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      dispatch(
        snackAdded({
          message: errorMessage,
          type: "error",
          duration: 5000,
        })
      );
    }
  };

  const isAiEvalRefineAgentAvailableAtPlan =
    currentPlan?.aiEvalAgent === "true" || currentPlan?.aiEvalAgent === true;

  useEffect(() => {
    if (formik.values?.insights) {
      setInsights(true);
      formik.setFieldValue("insights", formik.values.insights);
    }
  }, [formik.values.insights]);

  useEffect(() => {
    if (insights) {
      setStatsToggletext(
        "We'll research and add relevant stats and quotes to your AI article if they match your outline."
      );
      setPlaceholderText(
        "Add your personal experience, know-how, data and quotes to enrich the AI article. Add each insight in a separate row for better clarity."
      );
    } else {
      setStatsToggletext(
        "We'll research and add relevant stats and quotes to your AI article if they match your outline."
      );
      setPlaceholderText(
        "SEOwind will read and analyze content you paste here to learn how to communicate in your voice."
      );
    }
  }, [insights]);

  useEffect(() => {
    if (isAiResearchAgentAvailableAtPlan) {
      formik.setFieldValue("aiResearchAgent", true);
      setAiResearchAgentToggletext(
        "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
      );
    } else {
      setAiResearchAgentToggletext(
        "Upgrade to a higher plan to enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
      );
      formik.setFieldValue("aiResearchAgent", false);
    }
    if (isAiEvalRefineAgentAvailableAtPlan) {
      formik.setFieldValue("aiEvalRefineAgent", true);
      setAiEvalRefineAgentToggletext(
        "Enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
    } else {
      setAiEvalRefineAgentToggletext(
        "Upgrade to a higher plan and enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
      formik.setFieldValue("aiEvalRefineAgent", false);
    }
  }, [currentPlan]);

  const handleLocationChange = (
    _event: ChangeEvent<any>,
    value: (typeof locations)[0]
  ) => {
    formik.setFieldValue("location", value);
    localStorage.setItem("brief-location", JSON.stringify(value));

    // Reset state when location changes and it's not US
    if (value?.location_name !== "United States") {
      formik.setFieldValue("state", null);
      localStorage.removeItem("brief-state");
    }
  };

  const handleStateChange = (
    _event: ChangeEvent<any>,
    value: (typeof usStates)[0] | null
  ) => {
    formik.setFieldValue("state", value);
    if (value) {
      localStorage.setItem("brief-state", JSON.stringify(value));
    } else {
      localStorage.removeItem("brief-state");
    }
  };

  const handleLanguageChange = (event: ChangeEvent<any>) => {
    formik.handleChange(event);
    localStorage.setItem("brief-language", event.target.value);
  };

  /* projects commented out */
  const handleProjectChange = (_event: ChangeEvent<any>, value: any) => {
    if (value?.projectId === "new") {
      handleNewProject(_event as any);
      return;
    }
    formik.setFieldValue("project", value);
    localStorage.setItem("brief-project", JSON.stringify(value));
  };
  const handleNewProject = (event: any) => {
    event.stopPropagation();
    router.push({
      pathname: "/projects/settings/",
      query: {
        url: "new",
      },
    });
  };

  const redirectToProject = () => {
    if (Project?.projectId) {
      return `/app/projects/settings?url=edit&id=${Project?.projectId}`;
    }
    return "/app/projects";
  };

  useEffect(() => {
    if (Project?.isGSCIntegrated && isInterLinkingAvailableAtPlan) {
      setIsInterLinkingAdded(true);
      formik.setFieldValue("interlinking", true);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${Project?.projectName}` +
          "</a> Project Settings."
      );
    } else if (!Project?.isGSCIntegrated && isInterLinkingAvailableAtPlan) {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${Project?.projectName}` +
          "</a> Project Settings."
      );
    } else {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. Upgrade your plan to unlock the feature."
      );
    }
  }, [Project, isInterLinkingAvailableAtPlan]);

  useEffect(() => {
    dispatch(getProjects());
  }, []);

  // Keyword suggestions handlers
  const handleSuggestionsClick = () => {
    const keyword = formik.values.keyword?.trim();
    if (!keyword || keyword.length < 2) {
      return;
    }

    const locationName =
      formik.values.location?.location_name || "United States";
    const languageCode = formik.values.language || "English";

    // Convert language name to code
    const languageObj = languages.find(
      (lang) => lang.languageName === languageCode
    );
    const languageCodeForAPI = languageObj?.languageCode || "en";

    dispatch(
      fetchKeywordSuggestions({
        keyword,
        locationName,
        languageCode: languageCodeForAPI,
      })
    );
    setSuggestionsOpen(true);
  };

  const handleSuggestionSelect = (selectedKeyword: string) => {
    formik.setFieldValue("keyword", selectedKeyword);
    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  const handleSuggestionsClose = () => {
    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  // Handle manual typing in the keyword field
  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    // Close suggestions when user manually types
    if (suggestionsOpen) {
      handleSuggestionsClose();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsOpen) {
        const target = event.target as Node;
        const isClickInInput = keywordInputRef.current?.contains(target);
        const isClickInDropdown = dropdownRef.current?.contains(target);

        // Only close if click is outside both input and dropdown
        if (!isClickInInput && !isClickInDropdown) {
          handleSuggestionsClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestionsOpen]);

  // console.log("Projects in keywords", Projects);

  return (
    <Box sx={{ p: 3, pt: 4 }}>
      <Typography align="center" gutterBottom variant="h5">
        Update Your Article
      </Typography>
      <Typography align="center" gutterBottom variant="body1">
        Our AI workflow with AI agents will audit, research, rewrite, optimize
        for E-E-A-T, and add internal links.
      </Typography>
      <Box sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Focus keyword"
          name="keyword"
          type="text"
          value={formik.values.keyword}
          defaultValue={formik.values.keyword}
          onBlur={formik.handleBlur}
          onChange={handleKeywordChange}
          inputRef={keywordInputRef}
          helperText={
            formik.errors.keyword && formik.touched
              ? formik.errors.keyword
              : undefined
          }
          sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    !formik.values.keyword?.trim()
                      ? "Enter a keyword to get suggestions"
                      : formik.values.keyword.length < 2
                      ? "Keyword must be at least 2 characters"
                      : suggestionsLoading
                      ? "Loading suggestions..."
                      : "Get keyword suggestions"
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleSuggestionsClick}
                      disabled={
                        !formik.values.keyword?.trim() ||
                        formik.values.keyword.length < 2 ||
                        suggestionsLoading
                      }
                      sx={{
                        color: "#5048E5",
                        "&:hover": {
                          backgroundColor: "rgba(80, 72, 229, 0.1)",
                        },
                        "&:disabled": {
                          color: "#ccc",
                        },
                      }}
                    >
                      {suggestionsLoading ? (
                        <CircularProgress size={20} sx={{ color: "#5048E5" }} />
                      ) : (
                        <LightbulbIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {/* Keyword Suggestions Dropdown */}
        <KeywordSuggestionsDropdown
          anchorEl={keywordInputRef.current}
          open={suggestionsOpen}
          suggestions={keywordSuggestions}
          loading={suggestionsLoading}
          error={suggestionsError}
          onSuggestionSelect={handleSuggestionSelect}
          onClose={handleSuggestionsClose}
          dropdownRef={dropdownRef}
        />
      </Box>
      <Box sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Article Link"
          name="articleLink"
          type="text"
          value={formik.values.articleLink}
          defaultValue={formik.values.articleLink}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          helperText={
            formik.errors.articleLink && formik.touched
              ? formik.errors.articleLink
              : undefined
          }
          sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
        />
      </Box>
      <Grid container gap={1} sx={{ pt: 3 }}>
        <Grid>
          <FormControl fullWidth>
            <Autocomplete
              id="location"
              options={locations}
              getOptionLabel={(option: {
                location_name: string;
                country_iso_code: string;
              }) => option.location_name}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  helperText={
                    formik.errors.location && formik.touched
                      ? formik.errors.location
                      : undefined
                  }
                  sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
                />
              )}
              value={formik.values.location}
              onChange={handleLocationChange}
              renderOption={(props, option) => (
                <MenuItem
                  key={option.location_name}
                  value={option.location_name}
                  {...props}
                >
                  {option.location_name}
                </MenuItem>
              )}
            />
          </FormControl>
        </Grid>
        <Grid flex={1}>
          <FormControl fullWidth>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              id="language"
              value={formik.values.language}
              label="language"
              name="language"
              onChange={handleLanguageChange}
              defaultValue="English"
            >
              {languages
                .sort((a, b) => {
                  if (a.languageName > b.languageName) {
                    return 1;
                  } else if (a.languageName < b.languageName) {
                    return -1;
                  } else {
                    return 0;
                  }
                })
                .map((value) => (
                  <MenuItem key={value.languageName} value={value.languageName}>
                    {value.languageName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* US State Dropdown - appears below when United States is selected */}
      <Collapse
        in={formik.values.location?.location_name === "United States"}
        timeout={300}
      >
        <Grid container gap={1} sx={{ pt: 3 }}>
          <Grid>
            <FormControl>
              <Autocomplete
                id="state"
                options={usStates}
                getOptionLabel={(option: (typeof usStates)[0]) =>
                  option.state_name
                }
                isOptionEqualToValue={(option, value) =>
                  option.state_code === value?.state_code
                }
                PopperComponent={(props) => (
                  <Popper {...props} placement="bottom-start" />
                )}
                sx={{
                  width: 300,
                  "& .MuiOutlinedInput-root": {
                    boxShadow:
                      formik.values.location?.location_name === "United States"
                        ? "0 0 0 2px rgba(80, 72, 229, 0.1)"
                        : "none",
                    transition: "box-shadow 0.3s ease-in-out",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State (Optional)"
                    placeholder="Select a state for more targeted results"
                    helperText={
                      formik.errors.state && formik.touched
                        ? formik.errors.state
                        : "Leave blank for country-wide targeting"
                    }
                    sx={{
                      "& .MuiFormHelperText-root": {
                        color:
                          formik.errors.state && formik.touched
                            ? "#D14343"
                            : "#7B85AD",
                      },
                    }}
                  />
                )}
                value={formik.values.state}
                onChange={handleStateChange}
                renderOption={(props, option) => (
                  <MenuItem
                    key={option.state_code}
                    value={option.state_name}
                    {...props}
                  >
                    {option.state_name}
                  </MenuItem>
                )}
                clearOnEscape
                disableClearable={false}
              />
            </FormControl>
          </Grid>
          <Grid flex={1}>
            <Box />
          </Grid>
        </Grid>
      </Collapse>

      {/* projects commented out */}
      <Box sx={{ pt: 3 }}>
        <FormControl fullWidth>
          <Autocomplete
            id="project"
            options={updatedProjects}
            getOptionLabel={(option: {
              projectName: string;
              projectDomain: string;
              projectId: string;
            }) =>
              // Value selected with enter, right from the input
              option.projectName
            }
            sx={{ width: 552 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose a project"
                helperText={
                  formik.errors.project && formik.touched.project
                    ? formik.errors.project
                    : undefined
                }
                sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
              />
            )}
            value={formik.values.project}
            onChange={handleProjectChange}
            filterOptions={(options, params) => {
              let filtered = filter(options, params);
              if (params.inputValue !== "") {
                //show projecy with projectId new
                filtered.push(updatedProjects[updatedProjects.length - 1]);
              }
              return filtered;
            }}
            selectOnFocus
            freeSolo
            renderOption={(props, option) => (
              <>
                {option.projectId === "new" ? (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    <Box
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        width: "100%",
                        // paddingLeft: "1rem",
                      }}
                    >
                      <Button
                        variant="outlined"
                        sx={{
                          fontSize: "11px",
                          fontWeight: "600",
                          fontFamily: "Inter",
                          color: "#5048E5",
                          float: "left",
                          marginTop: "0.5rem",
                          // marginBottom: "1.5rem",
                          borderRadius: "4px",
                          padding: "8px",
                        }}
                        onClick={(event) => {
                          handleNewProject(event);
                        }}
                      >
                        <span style={{ marginRight: "0.5rem" }}>+</span>
                        Add New Project
                      </Button>
                    </Box>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    {option.projectName}
                  </MenuItem>
                )}
              </>
            )}
          />
        </FormControl>
      </Box>
      <Box sx={{ pt: 3 }}>
        <Typography
          gutterBottom
          sx={{ fontWeight: 500, fontSize: "1.1rem", mr: 1 }}
        >
          Update the article with:
        </Typography>
        {/* interlinking toggle */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  // disabled={!isInterLinkingAdded}
                  checked={formik.values.interlinking}
                  // defaultChecked={interLinkingChecked}
                  name="interlinking"
                  value={formik.values.interlinking}
                  disabled={!isInterLinkingAdded}
                />
              }
              label="Internal Linking"
            />
          </FormGroup>
          {/* add tool tip  */}
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    interLinkingToggletext ||
                    "We'll add internal links to your AI article if they match your outline.",
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>

        {/* AI Research Agent */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  disabled={!isAiResearchAgentAvailableAtPlan}
                  checked={formik.values?.aiResearchAgent}
                  name="aiResearchAgent"
                  value={formik.values?.aiResearchAgent}
                />
              }
              label="AI Research Agent"
            />
          </FormGroup>
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    aiResearchAgentToggletext ||
                    "Upgrade to a higher plan to enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible.",
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>
        {/* a sublist of tasks for ai research agent with icons check */}
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{
            ml: "0.5rem",
            mt: "-0.5rem",
          }}
        >
          <List sx={{ px: 0.3 }}>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiResearchAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Statistics & Quotes"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiResearchAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Thought Leadership Insights"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiResearchAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Case Studies"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiResearchAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Emerging Trends"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values.project?.companyDetails &&
                formik.values?.aiResearchAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Insights from Your Website"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
          </List>
        </Box>

        {/* AI Eval & Refine Agent  */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  disabled={!isAiEvalRefineAgentAvailableAtPlan}
                  checked={formik.values?.aiEvalRefineAgent}
                  // defaultChecked={interLinkingChecked}
                  name="aiEvalRefineAgent"
                  value={formik.values?.aiEvalRefineAgent}
                />
              }
              label="AI Eval & Refine Agent"
            />
          </FormGroup>
          {/* add tool tip  */}
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html: aiEvalRefineAgentToggletext,
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>
        {/* a sublist of tasks for ai eval & refine agent with icons check */}
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{
            ml: "0.5rem",
            mt: "-0.5rem",
          }}
        >
          <List sx={{ px: 0.3 }}>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Reviews initial article and provides feedback"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Gathers additional data and insights"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Rewrites & refines the final article for E-E-A-T"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
          </List>
        </Box>

        {/* humanize toggle */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  disabled={!isHumanizeAvailableAtPlan}
                  checked={formik.values.humanize}
                  name="humanize"
                  value={formik.values.humanize}
                />
              }
              label="AI Humanizer & Proofreader"
            />
          </FormGroup>
          {/* add tool tip  */}
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    humanizeToggletext ||
                    "We'll add your personal experience, know-how, data and quotes to enrich the AI article.",
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>

        {/* insights toggle */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleInsightsAdded}
                  // disabled={!isInsightsAdded}
                  checked={insights}
                  defaultChecked={insights}
                />
              }
              label="Your insights and instructions"
            />
          </FormGroup>
          {/* add tool tip  */}
          {/* <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        interLinkingToggletext ||
                        "We'll add internal links to your AI article if they match your outline.",
                    }}
                  />
                }
                placement="top"
              >
                <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
              </Tooltip> */}
        </Box>

        {insights && (
          <Box
            // display={"flex"}
            alignItems={"center"}
            width="100%"
            // height="170px"
          >
            <Box width="100%" height="53px" top="4px" gap="4px">
              <Typography
                color="#121828"
                sx={{
                  fontWeight: "500",
                  fontFamily: "Inter",
                  fontSize: "16px",
                }}
              >
                Add your insights and instructions
              </Typography>
              <Typography
                color="#7B85AD"
                sx={{
                  fontWeight: "400",
                  fontFamily: "Inter",
                  fontSize: "12px",
                }}
              >
                Add your instructions and expert insights (data, quotes,
                personal experience) to guide AI content. Write each instruction
                or insight on a separate line for clarity.
              </Typography>
            </Box>
            <Box width="100%" marginTop={"10px"}>
              <InsightsEditor
                value={formik.values.insights}
                onChange={(value) => formik.setFieldValue("insights", value)}
                placeholder={placeholderText}
                error={
                  formik.touched.insights && Boolean(formik.errors.insights)
                }
                helperText={
                  formik.touched.insights && formik.errors.insights
                    ? formik.errors.insights
                    : undefined
                }
                compact={true}
                maxLength={20000}
                showResizeHandle={true}
                onWriteInstructions={handleWriteInstructions}
                isGeneratingInstructions={Boolean(isOptimizeContentLoading)}
                dispatch={dispatch}
              />
            </Box>
            {/* <Box
            width="100%"
            display={"flex"}
            sx={{
              ml: 1,
              mt: 1,
            }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              width={"100%"}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => setIsInsightsDialogOpen(true)}
            > */}
            {/* <Box
                sx={{
                  mt: 0.5,
                  height: "12px",
                  width: "12px",
                  // cursor: "pointer",
                }}
              >
                <ExpandModal
                  sx={
                    {
                      // height: "24px",
                      // width: "24px",
                    }
                  }
                />
              </Box>
              <Box
                sx={{
                  ml: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "Inter",
                    color: "#5048E5",
                    // fontStyle: "normal",
                    // lineHeight: "normal",
                    textAlign: "right",
                  }}
                >
                  Expand field
                </Typography>
              </Box> */}
            {/* </Box> */}
          </Box>
        )}
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Button variant="contained" type="submit">
          Update Article
        </Button>
      </Box>
    </Box>
  );
};

export default ChooseKeyword;
